import styled, { css } from 'styled-components';
import { th, variants } from 'style/theme';

const porscheVariant = css`
  --base: ${th('colors.porsche-2')};
  --base-secondary: ${th('colors.porsche-4')};
  --base-focus: ${th('colors.porsche-weak')};
  --text-color: ${th('colors.white')};
`;

const pictonBlueVariant = css`
  --base: ${th('colors.pictonBlue0')};
  --base-secondary: ${th('colors.pictonBlue-2')};
  --base-focus: ${th('colors.pictonBlue0')};
  --text-color: ${th('colors.white')};
`;

const deepBlushVariant = css`
  --base: ${th('colors.deepBlush')};
  --base-secondary: ${th('colors.deepBlush-2')};
  --base-focus: ${th('colors.deepBlush')};
  --text-color: ${th('colors.white')};
`;

const fountainBlueVariant = css`
  --base: ${th('colors.turquoise')};
  --base-secondary: ${th('colors.fountainBlue-2')};
  --base-weak: ${th('colors.fountainBlue+4')};
  --base-focus: ${th('colors.fountainBlue+2')};
  --text-color: ${th('colors.white')};
`;

const fuzzyWuzzyBrownVariant = css`
  --base: ${th('colors.fuzzyWuzzyBrown')};
  --base-secondary: ${th('colors.fuzzyWuzzyBrown-2')};
  --base-weak: ${th('colors.fuzzyWuzzyBrown+4')};
  --base-focus: ${th('colors.fuzzyWuzzyBrown+4')};
  --text-color: ${th('colors.white')};
`;

const pickledBluewoodVariant = css`
  --base: ${th('colors.pickledBluewood')};
  --base-secondary: ${th('colors.pickledBluewood-2')};
  --base-weak: rgba(0.1529, 0.2353, 0.2863, 0.12);
  --base-focus: ${th('colors.pickledBluewood3')};
  --text-color: ${th('colors.white')};
`;

const confettiVariant = css`
  --base: ${th('colors.confetti')};
  --base-secondary: ${th('colors.confetti-2')};
  --base-focus: ${th('colors.confetti+4')};
  --text-color: ${th('colors.pickledBluewood')};
`;

const transparentWhiteVariant = css`
  --base: ${th('colors.transparentWhite.4')};
  --text-color: ${th('colors.pickledBluewood')};
`;

const defaultVariant = css`
  background-color: var(--base);
  color: var(--text-color);

  :hover,
  :active,
  :focus {
    background-color: var(--base-secondary);
  }

  :focus {
    outline: 2px solid var(--base-focus);
  }

  ${({ disabled }) =>
    disabled &&
    css`
      background-color: ${th('colors.pickledBluewood3')};
      :hover {
        background-color: ${th('colors.pickledBluewood3')};
      }
    `}
`;

const textOnlyVariant = css`
  background-color: transparent;
  color: var(--base);

  :hover,
  :active,
  :focus {
    background-color: var(--base-weak);
  }

  :focus {
    outline: 2px solid var(--base-weak);
  }

  ${({ disabled }) =>
    disabled &&
    css`
      color: ${th('colors.pickledBluewood3')};

      :hover {
        background-color: transparent;
      }
    `}
`;

const underlineVariant = css`
  padding: 0;
  border-radius: 0;
  border-bottom: 1px solid;
  border-color: var(--base);
  background-color: transparent;
  color: var(--base);
  line-height: 1;

  ${({ disabled }) =>
    disabled &&
    css`
      color: ${th('colors.pickledBluewood3')};
    `}
`;

const outlineVariant = css`
  background-color: transparent;
  color: var(--base);
  box-shadow: inset 0px 0px 0px 1.5px var(--base);

  :hover,
  :active,
  :focus {
    background-color: var(--base-weak);
  }

  :focus {
    outline: 1.5px solid var(--base-focus);
  }
`;

const outlineWithTextColorVariant = css`
  background-color: transparent;
  color: var(--text-color);
  box-shadow: inset 0px 0px 0px 1.5px var(--base);
`;

const transparentOutlineVariant = css`
  background-color: transparent;
  color: var(--base);
  box-shadow: inset 0px 0px 0px 1.5px var(--base-weak);

  :hover,
  :active {
    background-color: var(--base-weak);
  }

  :focus {
    outline: 1.5px solid var(--base-focus);
  }

  ${({ disabled }) =>
    disabled &&
    css`
      :hover {
        background-color: transparent;
      }
    `}
`;

export const StyledButton = styled.button`
  display: flex;
  flex-direction: row;
  gap: 4px;
  justify-content: center;
  align-items: center;
  border: none;
  font-size: 16px;
  font-family: 'Helvetica Now';
  font-weight: 500;
  transition: background-color 200ms, color 200ms;
  cursor: pointer;

  ${({ disabled }) =>
    disabled &&
    css`
      cursor: default;
    `}

  ${variants('color', {
    'porsche-2': porscheVariant,
    pictonBlue: pictonBlueVariant,
    deepBlush: deepBlushVariant,
    fountainBlue: fountainBlueVariant,
    fuzzyWuzzyBrown: fuzzyWuzzyBrownVariant,
    pickledBluewood: pickledBluewoodVariant,
    confetti: confettiVariant,
    transparentWhite: transparentWhiteVariant
  })};

  ${variants('thickness', {
    default: css`
      padding: 12px 20px;
      border-radius: 12px;
    `,
    thin: css`
      padding: 8px 16px;
      border-radius: 12px;
    `
  })};

  ${variants('variant', {
    default: defaultVariant,
    outline: outlineVariant,
    outlineWithText: outlineWithTextColorVariant,
    transparentOutline: transparentOutlineVariant,
    textOnly: textOnlyVariant,
    underline: underlineVariant
  })};

  > a {
    text-decoration: none;
  }

  :disabled {
    cursor: default;
  }
`;
